import { createFileRoute, useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/payroll/advances")({
  component: RouteComponent,
});

function RouteComponent() {
  // SalaryAdvanceViewSet is a whole-class gate with no degrade-to-own-rows
  // fallback, so the whole page is protected up front.
  return (
    <ProtectedRoute requiredPermission="salary.advance.manage">
      <AdvancesPage />
    </ProtectedRoute>
  );
}

import { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { ProtectedRoute } from "@/components/protected-route";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import { Input } from "@/components/ui/input";
import PageHeader from "@/components/reusable-components/header/page-header";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { AdvanceTable } from "@/components/crud/table/AdvanceTable";
import { AdvanceFormSheet } from "@/components/crud/forms/AdvanceFormSheet";

import useFetchAdvances from "@/hooks/api/salaries/advances/useFetchAdvances";
import useCreateAdvance from "@/hooks/api/salaries/advances/useCreateAdvance";
import useUpdateAdvance from "@/hooks/api/salaries/advances/useUpdateAdvance";
import useDeleteAdvance from "@/hooks/api/salaries/advances/useDeleteAdvance";
import useFetchAuditLogs from "@/hooks/api/salaries/line-items/useFetchAuditLogs";
import useAutoCompleteUser from "@/hooks/api/user/useAutoCompleteUser";
import { useDebounce } from "@/hooks/use-debounce";

import type { SalaryAdvance, SalaryAdvancePayload } from "@/types/user/salary";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";

function AdvancesPage() {
  const navigate = useNavigate();
  const { data: capabilities } = useMyPermissions();
  const canViewAudit = can(capabilities, "salary.audit.view");

  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, resetDebounce] = useDebounce(searchQuery, 500);
  const [showFilters, setShowFilters] = useState(false);
  const [settledFilter, setSettledFilter] = useState<string>("all");

  const advancesQuery = useFetchAdvances({
    page_size: pageSize,
    page,
    search: debouncedSearch || undefined,
  });
  const count = advancesQuery.data?.count ?? 0;

  const createAdvance = useCreateAdvance();
  const updateAdvance = useUpdateAdvance();
  const deleteAdvance = useDeleteAdvance();

  const totalPages = useMemo(
    () => (count ? Math.ceil(count / pageSize) : 1),
    [count, pageSize]
  );

  const isMutating =
    createAdvance.isPending ||
    updateAdvance.isPending ||
    deleteAdvance.isPending;
  const isLoading = advancesQuery.isPending || isMutating;
  const isFetching = advancesQuery.isFetching || isMutating;

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingAdvance, setEditingAdvance] = useState<SalaryAdvance | null>(
    null
  );
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SalaryAdvance | null>(null);

  const auditLogsQuery = useFetchAuditLogs(
    editingAdvance
      ? { entity_type: "salary_advance", entity_id: String(editingAdvance.id) }
      : undefined,
    { enabled: canViewAudit && isViewOnly && editingAdvance != null }
  );
  const auditLogs = auditLogsQuery.data?.results ?? [];

  const [userSearchQuery, setUserSearchQuery] = useState("");
  const autoCompleteQuery = useAutoCompleteUser(
    { search: userSearchQuery || undefined },
    { enabled: isSheetOpen }
  );
  const users = useMemo(
    () => autoCompleteQuery.data?.pages.flatMap((p) => p.results) ?? [],
    [autoCompleteQuery.data]
  );
  const autoCompleteLoading = autoCompleteQuery.isFetching;

  // Back to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, setPage]);

  const resetFilters = () => {
    setSearchQuery("");
    setSettledFilter("all");
    resetDebounce();
    setPage(1);
  };

  // is_settled is a backend @property — not a DB column — so filter client-side
  const displayedAdvances = useMemo(() => {
    const list = advancesQuery.data?.results ?? [];
    if (settledFilter === "active") return list.filter((a) => !a.is_settled);
    if (settledFilter === "settled") return list.filter((a) => a.is_settled);
    return list;
  }, [advancesQuery.data, settledFilter]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  const handleSearchUsers = useCallback((query: string) => {
    setUserSearchQuery(query);
  }, []);

  const handleCreate = () => {
    setEditingAdvance(null);
    setIsViewOnly(false);
    setIsSheetOpen(true);
  };

  const handleEdit = (advance: SalaryAdvance) => {
    setEditingAdvance({ ...advance });
    setIsViewOnly(false);
    setIsSheetOpen(true);
  };

  const handleView = (advance: SalaryAdvance) => {
    setEditingAdvance({ ...advance });
    setIsViewOnly(true);
    setIsSheetOpen(true);
  };

  const handleDelete = (advance: SalaryAdvance) => {
    setDeleteTarget(advance);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteAdvance.mutateAsync({ id: deleteTarget.id });
      toast.success("Advance deleted");
      setDeleteTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleFormSubmit = async (data: SalaryAdvancePayload) => {
    if (editingAdvance) {
      await updateAdvance.mutateAsync({
        id: editingAdvance.id,
        payload: { amount: data.amount, note: data.note },
      });
      toast.success("Advance updated");
    } else {
      await createAdvance.mutateAsync(data);
      toast.success("Advance created");
    }
    setIsSheetOpen(false);
  };

  return (
    <SidebarInset>
      <DashboardHeader />

      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Salary Advances"
          description="Manage employee salary advances and track repayments."
          isLoading={isLoading}
          onToggleFilter={() => setShowFilters((prev) => !prev)}
          onAdd={handleCreate}
          addLabel="New Advance"
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <Input
            placeholder="Search by employee..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-64"
            disabled={isFetching}
          />
          <Select
            value={settledFilter}
            onValueChange={setSettledFilter}
            disabled={isFetching}
          >
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="settled">Settled</SelectItem>
            </SelectContent>
          </Select>
        </FiltersPanel>

        <AdvanceTable
          advances={displayedAdvances}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
          isLoading={isLoading}
          onNameClick={(advance) =>
            navigate({
              to: "/admin/payroll/advances/$userId",
              params: { userId: String(advance.user) },
            })
          }
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={handlePageChange}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        <AdvanceFormSheet
          isOpen={isSheetOpen}
          onClose={() => setIsSheetOpen(false)}
          onSubmit={handleFormSubmit}
          editingAdvance={editingAdvance}
          isViewOnly={isViewOnly}
          isLoading={isMutating}
          auditLogs={auditLogs}
          auditLoading={auditLogsQuery.isPending}
          canViewAudit={canViewAudit}
          users={users}
          onSearchUsers={handleSearchUsers}
          autoCompleteLoading={autoCompleteLoading}
        />

        <DeleteConfirmDialog
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
          title="Delete Advance"
          description={`Delete the advance for ${deleteTarget?.user_full_name}? All repayment records will also be removed.`}
          isLoading={deleteAdvance.isPending}
          destructive
        />
      </div>
    </SidebarInset>
  );
}
